<template>
  <div v-loading="categoryLoading" class="bg-new p-4 pt-0" style="height: 93vh" element-loading-background="transparent">
    <!-- Product category overview -->
    <div>
      <div class="d-flex justify-content-between align-items-center p-4 pt-5">
        <h2 class="fw-bold">{{ $t('views.configurator.step0') }}</h2>
        <div class="w-25">
          <el-input v-model="searchText" :placeholder="$t('views.template.search')" />
        </div>
      </div>
      <!-- Product categories -->
      <div class="p-2 ps-4 product-grid">
        <div v-for="product in productTypeWithFilter" :key="product.productId" class="product-card__wrapper">
          <product-card
            :title="product.type.name"
            :primary-button-text="$t('views.customize.workflow.productOverview.productCardButton')"
            @button-clicked="this.router.push(`/?productTypeId=${product.type.productTypeId}`)"
          >
            <preview-renderer-img
              :meshes="[
                {
                  thumbnail: product.name,
                  thumbnailUrl: product.thumbnail.content,
                },
              ]"
              img-size="5"
            />
          </product-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';

import LayoutHeader from '@/components/layout/Header.vue';
import LoaderCover from '@/components/features/LoaderCover.vue';
import ProductCard from '../layout/sidebar/ProductCard.vue';
import { Product } from '@/types/api/Model/Product';
import PreviewRendererImg from '@/components/features/previewRenderer/PreviewRendererImg.vue';
import { Model2 } from '@/types/api/Model/Model';
import { useProductStore } from '@/stores/productStore';
import * as modelService from '@/services/api/modelService';
import { useUserStore } from '@/stores/userStore';
import { useRoute, useRouter } from 'vue-router';

@Component({
  components: {
    LoaderCover,
    LayoutHeader,
    ProductCard,
    PreviewRendererImg,
  },
  emits: ['showProductTypes', 'selectedProductType'],
})

export default class CustomizeOverview extends Vue {
  router = useRouter();
  route = useRoute();
  authSystemCheck = false;
  categoryLoading = false;
  modelList: Model2[] = [];
  isHomepage = false;

  productStore = useProductStore();
  userStore = useUserStore();
  searchText = '';

  async mounted(): Promise<void> {
    this.categoryLoading = true;
    this.isHomepage = this.route.params.supplier != undefined;

    try {
      const models = await this.getModels(); 
      this.modelList = models.filter((item) => !item.basedOn);
      this.productStore.setProductTypes(this.getProductTypes());
    } catch (error) {
      console.error('Failed to get product models:', error);
      this.modelList = [];
    } finally {
      this.categoryLoading = false;
    }
  }

  async getModels() {
    const user = this.userStore.user;
    const domain = this.userStore.domain;

    if (user?.pk) {
      return await modelService.getModelsByUserId({
        userId: user.pk,
        productTypeId: 1
      });
    } else if (domain) {
      return await modelService.getModelsByUrlSuffix(domain.urlSuffix!, null);
    } else {
      return [];
    }
  }

  getProductTypes(): Product[] {
    const productTypeList: Product[] = [];
    for (const model of this.modelList) {
      if (productTypeList.findIndex((pt) => pt.type.productTypeId == model.product.type.productTypeId) === -1) {
        productTypeList.push(model.product);
      }
    }

    return productTypeList;
  }

  get productTypeWithFilter(): Product[] {
    const productTypes = this.getProductTypes();
    if (this.searchText.length > 0) {
      return productTypes.filter((item) => item.name.toLowerCase().includes(this.searchText.toLowerCase()));
    }
    return productTypes;
  }
}
</script>

<style lang="scss" scoped>
.bg-new {
  background-color: #ffffff;
  height: 100vh;
}

.content-list {
  position: relative;
}

.product-grid {
  display: flex;
  flex-wrap: wrap; /* Allows wrapping when max-width is reached */
  justify-content: flex-start; /* Aligns items to the left */
  gap: 0.8rem;
  padding: 1.58rem;
  max-width: 90vw;
}

.product-card__wrapper {
  height: fit-content;
  width: fit-content;
  justify-content: flex-start;
}
</style>
